import { useEffect, useRef, useState } from "react";
import type { Suggestion } from "@/api/types";
import { useSuggestions } from "@/features/data";
import { useDebouncedValue } from "@/lib/useDebounce";
import { ColorDot, Input } from "@/components/ui";
import { cn } from "@/lib/cn";

interface DescriptionInputProps {
  value: string;
  onChange: (v: string) => void;
  /** onPick fills the whole form from a recent entry (description, project, billable, tags). */
  onPick: (s: Suggestion) => void;
  /** onEnter fires on Enter when no suggestion is highlighted (e.g. start the timer). */
  onEnter?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** bare = borderless (timer bar); field = standard form input. */
  variant?: "bare" | "field";
}

let seq = 0;

/** DescriptionInput is a combobox over the user's recent entries. It opens on
 * focus (the API returns recents for an empty query) and refines as you type.
 * A plain positioned listbox, not a Radix Popover — the input must keep focus. */
export function DescriptionInput({ value, onChange, onPick, onEnter, placeholder, autoFocus, variant = "field" }: DescriptionInputProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [idBase] = useState(() => `desc-sugg-${++seq}`);
  const wrapRef = useRef<HTMLDivElement>(null);
  const q = useDebouncedValue(value, 200);
  const { data: items = [] } = useSuggestions(q, open);

  useEffect(() => setActive(-1), [items]);

  const expanded = open && items.length > 0;

  function pick(s: Suggestion) {
    onPick(s);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (!items.length) return;
      const delta = e.key === "ArrowDown" ? 1 : -1;
      setActive((a) => (a + delta + items.length) % items.length);
    } else if (e.key === "Enter") {
      if (expanded && active >= 0) {
        e.preventDefault();
        pick(items[active]);
      } else {
        onEnter?.();
      }
    } else if (e.key === "Escape") {
      // Close only the listbox; don't let a parent Dialog dismiss too.
      if (expanded) {
        e.stopPropagation();
        setOpen(false);
      }
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  }

  const inputProps = {
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
      setOpen(true);
    },
    onFocus: () => setOpen(true),
    onKeyDown,
    placeholder,
    autoFocus,
    role: "combobox" as const,
    "aria-expanded": expanded,
    "aria-controls": idBase,
    "aria-activedescendant": expanded && active >= 0 ? `${idBase}-${active}` : undefined,
    "aria-autocomplete": "list" as const,
  };

  return (
    <div
      ref={wrapRef}
      className={cn("relative", variant === "bare" && "min-w-0 flex-1")}
      onBlur={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      {variant === "bare" ? (
        <input {...inputProps} className="h-9 w-full min-w-0 bg-transparent text-sm text-ink placeholder:text-faint focus:outline-none" />
      ) : (
        <Input {...inputProps} />
      )}
      {expanded && (
        <ul
          id={idBase}
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 max-h-64 w-full min-w-72 overflow-auto rounded-lg border border-line bg-elevated py-1 shadow-xl"
        >
          {items.map((s, i) => (
            <li
              key={`${s.description}·${s.project_id ?? ""}`}
              id={`${idBase}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus in the input
                pick(s);
              }}
              onMouseEnter={() => setActive(i)}
              className={cn("flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm", i === active && "bg-surface")}
            >
              <span className="truncate text-ink">{s.description}</span>
              {s.project_name && (
                <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 text-xs text-faint">
                  <ColorDot color={s.project_color ?? "#888"} />
                  <span className="max-w-32 truncate">{s.project_name}</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
